import { useState, type CSSProperties } from "react";
import * as si from "simple-icons";
import type { SimpleIcon } from "simple-icons";
import { CUSTOM_ICONS } from "./icons/customIcons";
import styles from "./Logo.module.css";

type LogoProps = {
  slug: string;
  size?: number;
  brandHex?: string | null;
  // Real brand logo image — Composio's CDN in prod, our mirror in
  // local-dev. When present and loadable it wins over the simple-icons
  // glyph; a load failure falls back gracefully.
  logoUrl?: string | null;
  // "chip" (default): the near-white app-icon tile used across the catalog,
  // with the real brand logo (logoUrl) inset at 64%.
  // "brand": a brand-colour tile with a white monochrome glyph. Uniform app
  // icons regardless of the source asset's shape - used by the channel rows
  // (hero, collapsed bar, cards) where heterogeneous PNGs (filled circles vs
  // bare marks) never sit cleanly on a white tile.
  variant?: "chip" | "brand";
};

type ResolvedIcon = {
  svg: string;
  hex: string;
  title: string;
  // Carries its own brand colours (e.g. the Slack rainbow); never recoloured.
  multicolor: boolean;
};

const capitalize = (s: string): string =>
  s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);

const lookupSimpleIcon = (slug: string): SimpleIcon | undefined => {
  const key = `si${capitalize(slug)}`;
  const table = si as unknown as Record<string, SimpleIcon | undefined>;
  return table[key];
};

const resolveIcon = (slug: string): ResolvedIcon | undefined => {
  const custom = CUSTOM_ICONS[slug];
  if (custom !== undefined) {
    return {
      svg: custom.svg,
      hex: custom.hex,
      title: slug,
      multicolor: custom.multicolor === true,
    };
  }
  const builtin = lookupSimpleIcon(slug);
  if (builtin !== undefined) {
    return {
      svg: builtin.svg,
      hex: builtin.hex,
      title: builtin.title,
      multicolor: false,
    };
  }
  return undefined;
};

// Perceived luminance (0-255). Brand marks are monochrome simple-icons
// glyphs; a very light brand colour would vanish on the light chip, so
// we swap it for a dark neutral when that happens.
const luminance = (hex: string): number => {
  const h = hex.length === 3
    ? hex.split("").map((c) => c + c).join("")
    : hex;
  const n = Number.parseInt(h, 16);
  if (Number.isNaN(n)) return 0;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

type ChipStyle = CSSProperties & {
  ["--chipBg"]?: string;
  ["--chipFg"]?: string;
  ["--chipBorder"]?: string;
};

// Radius scales with size but is capped so the 40px+ chips keep their current
// 11px corners; small hero chips (24-28px) would otherwise look near-circular
// at a fixed 11px radius.
const chipRadius = (size: number): number => Math.min(11, Math.round(size * 0.275));

// "chip": brand glyphs render on a near-white tile in BOTH themes — the same way
// app stores show product icons, keeping dark marks (GitHub, Notion) legible.
// "brand": the tile takes the brand colour and the glyph is white (or dark for
// very light brands) — a uniform app-icon look for the channel rows.
const chipStyle = (
  size: number,
  hex: string,
  variant: "chip" | "brand",
  multicolor: boolean,
): ChipStyle => {
  const radius = chipRadius(size);
  if (variant === "brand") {
    // A full-colour mark (Slack) only reads on a light tile, so render it on
    // white with a hairline that keeps the tile delineated against a light
    // page background (the brand-colour tiles need no such help).
    if (multicolor) {
      return {
        width: size,
        height: size,
        borderRadius: radius,
        ["--chipBg"]: "#ffffff",
        ["--chipFg"]: "#1a1a1c",
        ["--chipBorder"]: "rgba(20, 18, 15, 0.16)",
      };
    }
    const fg = luminance(hex) > 180 ? "1a1a1c" : "ffffff";
    return {
      width: size,
      height: size,
      borderRadius: radius,
      ["--chipBg"]: `#${hex}`,
      ["--chipFg"]: `#${fg}`,
      ["--chipBorder"]: "transparent",
    };
  }
  const fg = luminance(hex) > 215 ? "1a1a1c" : hex;
  return {
    width: size,
    height: size,
    borderRadius: radius,
    ["--chipBg"]: `color-mix(in srgb, #${hex} 8%, #ffffff)`,
    ["--chipFg"]: `#${fg}`,
    ["--chipBorder"]: `color-mix(in srgb, #${hex} 24%, transparent)`,
  };
};

export const Logo = ({
  slug,
  size = 40,
  brandHex,
  logoUrl,
  variant = "chip",
}: LogoProps): JSX.Element => {
  const [imgFailed, setImgFailed] = useState<boolean>(false);
  const icon = resolveIcon(slug);
  // Tolerate a leading '#': Composio-synced rows store bare hex, but the
  // hand-seeded natives (V31 servicem8, V50 simpro) store '#RRGGBB' - and
  // `color-mix(in srgb, ##062F87 ...)` is invalid CSS, which silently drops
  // the chip's background AND border (the raw-logo-no-white-box bug).
  const hex = (brandHex ?? icon?.hex ?? "5b6478").replace(/^#/, "");
  const multicolor = icon?.multicolor === true;
  const style = chipStyle(size, hex, variant, multicolor);
  const isBrand = variant === "brand";
  const chipClass = isBrand ? `${styles.chip} ${styles.brand}` : styles.chip;
  const svgClass = multicolor ? `${styles.svg} ${styles.multicolor}` : styles.svg;

  // 1. Real brand logo image — preferred when we have one and it loads. The
  // brand variant skips it: it always wants the monochrome glyph on a coloured
  // tile, never the (inconsistently shaped) source PNG.
  if (!isBrand && logoUrl !== undefined && logoUrl !== null && logoUrl !== "" && !imgFailed) {
    return (
      <span className={chipClass} style={style} role="img" aria-label={slug}>
        <img
          className={styles.img}
          src={logoUrl}
          alt=""
          loading="lazy"
          onError={(): void => setImgFailed(true)}
        />
      </span>
    );
  }

  // 2. simple-icons brand glyph.
  if (icon) {
    return (
      <span
        className={chipClass}
        style={style}
        role="img"
        aria-label={icon.title}
      >
        <span
          className={svgClass}
          // simple-icons ships sanitized SVG markup; our custom map is hand-crafted.
          dangerouslySetInnerHTML={{ __html: icon.svg }}
        />
      </span>
    );
  }

  // 3. Monogram fallback.
  const fallback = (slug[0] ?? "?").toUpperCase();
  return (
    <span className={chipClass} style={style} role="img" aria-label={slug}>
      <span className={styles.monogram} style={{ fontSize: size * 0.42 }}>
        {fallback}
      </span>
    </span>
  );
};
